import React, {useMemo, useState} from 'react';
import { Divider, Table, Typography, Row, Space } from 'antd';
import Button from '@mui/material/Button';
import get from 'lodash/get';
import CSVFile from "../../../../Components/CSVFile/CSVFile"; // Import lodash's get function for safe deep access
import ThirdPartyPortfolioExports from "../../../TradingSignals/ThirdPartyExports";
import {useEnsureTickersLoaded} from "../../../../hooks/useEnsureTickersLoaded";
import {DoubleLine} from "../../../../Components/Labels";
import Logo from "../../../../Components/Graphics/Logo";
import {stringMixin, money} from "../../../../utils/strings";
import {DownloadRounded, VerifiedRounded, CloudSyncRounded} from "@mui/icons-material";
import {SaveRounded} from "@mui/icons-material";
const { Text } = Typography;


const additionalMetricLookup = {
  'metrics.dy': {
    name: 'Div Yield',
    type: 'pct'
  }
}

const PreviewTable = ({ subset, originalWeights, buttonGroup, onSave }) => {
  const [showCSVExport, setShowCSVExport] = useState(false); // State to control CSV Export Modal visibility
  const toggleCSVExport = () => setShowCSVExport(!showCSVExport);

  const processedSubset = useMemo(() => {
    let cumulativeWeight = 0; // Reset cumulative weight for each processing
    return subset.map(item => {
      const wgt = parseFloat(item.wgt); // Ensure wgt is a number
      cumulativeWeight += wgt; // Accumulate weight
      return {
        ...item,
        cumulativeWeight, // Add cumulative weight to each item
      };
    });
  }, [subset]);

  const additionalColumns = Object.entries(additionalMetricLookup).reduce((acc, [dotPath, info]) => {
    const metricExists = processedSubset.some(item => get(item, dotPath) !== undefined);

    if (metricExists) {
      acc.push({
        title: info.name,
        key: dotPath,
        render: (_, record) => {
          const value = get(record, dotPath);
          return info.type === 'pct' ? `${(value * 100).toFixed(2)}%` : value;
        }
      });
    }

    return acc;
  }, []);

  const { tickers, loading } = useEnsureTickersLoaded(processedSubset.map(x => x.symbol));
  if (loading) {
        return <div>Loading...</div>;
  }


  const Name = ({symbol})=>{
    const ticker = tickers.find(x=>x.symbol === symbol);
    return <DoubleLine title={symbol.toUpperCase()} text={ticker?.name??"--"} />
  }
  // console.log(processedSubset)
  const columns = [
      {
          title: <Typography.Text strong style={{fontWeight: 14}}># ({subset.length})</Typography.Text>,
          dataIndex: 'symbol',
          key: 'logo',
          render: (symbol, record) => {
              const logoUrl = tickers.find(x=>x.symbol === symbol)?.logo;
              return (
                  logoUrl ? <Logo src={logoUrl} /> : ''
              )
          }
      },
    {
      title: 'Ticker',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol) => <Name symbol={symbol} />,
    },
    {
      title: 'Weight',
      dataIndex: 'wgt',
      key: 'wgt',
      render: (text) => `${(text * 100).toFixed(2)}%`,
    },
    {
      title: 'From',
      dataIndex: 'symbol', // Using 'symbol' to access originalWeights
      key: 'fromWeight',
      render: (text) => {
        const originalWeight = originalWeights?.[text];
        return originalWeight ? `${(originalWeight * 100).toFixed(2)}%` : 'N/A';
      },
    },
   {
      title: 'Total',
      dataIndex: 'cumulativeWeight', // Use pre-calculated cumulativeWeight
      key: 'cumulativeWeight',
      render: (cumulativeWeight) => `${(cumulativeWeight * 100).toFixed(2)}%`,
      // No sorter for cumulativeWeight as its sorting would be nonsensical
    },
    {
        title: "Mktcap",
        dataIndex: 'mktcap',
        key: 'mktcap',

        render: (_, record)=>stringMixin.largeNumber(tickers.find(x=>x.symbol === record.symbol)?.mktcap??0, 0)

    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
    },
    {
      title: 'ETFs',
      dataIndex: 'parents',
      key: 'etfs',
      render: (parents) => parents.map(x => <Text key={x}>{x.toUpperCase()}</Text>).reduce((prev, curr) => [prev, ', ', curr]),
    },
    ...additionalColumns

  ];

  const dataSource = processedSubset.map((ticker, index) => ({
    ...ticker,
    key: index,
  }));
  return (
        <div className={'card-1 px-4 py-2'}>
            <Row justify={'space-between'}>
                <Button color={'info'} size={'large'} startIcon={<SaveRounded/>} variant={'contained'}>Save SMA</Button>
                {buttonGroup}
                <Space size={'large'}>
                    <ThirdPartyPortfolioExports dataSource={dataSource} />
                    <Button startIcon={<DownloadRounded/>} onClick={toggleCSVExport}>Download Data</Button>
                </Space>
            </Row>
          <Divider />
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            size="small"
            scroll={{y: 800}}
          />
          <CSVFile
            dataSource={dataSource}
            initialColumns={columns}
            visible={showCSVExport}
            onClose={toggleCSVExport}
          />
        </div>
  );
};

export default PreviewTable;
